export const DEFAULT_MARKDOWN = `# Welcome to Markdown Viewer 👋

A fast, enterprise-grade markdown viewer with **GitHub Flavored Markdown**, Mermaid diagrams, and math support.

## Features

- ✅ **GitHub Flavored Markdown** - Full GFM spec compliance
- ✅ **Syntax Highlighting** - 190+ languages
- ✅ **Mermaid Diagrams** - Flowcharts, sequence diagrams, and more
- ✅ **Math Rendering** - LaTeX equations with KaTeX
- ✅ **Task Lists** - Interactive checkboxes
- ✅ **Themes** - Light and dark modes

## Quick Examples

### Text Formatting

**Bold text** and *italic text* and ~~strikethrough~~.

Inline \`code\` with backticks.

### Code Blocks

\`\`\`javascript
function greet(name) {
  console.log(\`Hello, \${name}!\`);
}

greet('World');
\`\`\`

### Tables

| Feature | Status |
|---------|--------|
| GFM | ✅ |
| Mermaid | ✅ |
| Math | ✅ |

### Task Lists

- [x] Setup project
- [x] Build core engine
- [ ] Deploy to production

### Blockquotes

> "The only way to do great work is to love what you do."
> — Steve Jobs

---

## Try It Out!

Click **"Load Sample"** in the toolbar to see more examples, or start typing your own markdown!
`;

export const SAMPLE_TEMPLATES = {
  basic: {
    name: 'Basic Example',
    content: DEFAULT_MARKDOWN,
  },
  mermaid: {
    name: 'Mermaid Diagrams',
    content: `# Mermaid Diagram Examples

## Flowchart

\`\`\`mermaid
graph TD
    A[Start] --> B{Is it working?}
    B -->|Yes| C[Great!]
    B -->|No| D[Debug]
    D --> B
    C --> E[End]
\`\`\`

## Sequence Diagram

\`\`\`mermaid
sequenceDiagram
    participant User
    participant App
    participant API

    User->>App: Open markdown
    App->>API: Render request
    API-->>App: HTML response
    App-->>User: Display preview
\`\`\`

## Gantt Chart

\`\`\`mermaid
gantt
    title Project Timeline
    dateFormat  YYYY-MM-DD

    section Phase 1
    Core Engine    :done,    p1, 2024-01-01, 7d
    Testing        :done,    p2, 2024-01-08, 3d

    section Phase 2
    Web App        :active,  p3, 2024-01-11, 7d
    Deployment     :         p4, 2024-01-18, 2d
\`\`\`

## Class Diagram

\`\`\`mermaid
classDiagram
    class MarkdownViewer {
        +String content
        +Theme theme
        +render()
        +toggle Theme()
    }
    class Renderer {
        +parse()
        +sanitize()
    }
    MarkdownViewer --> Renderer
\`\`\`
`,
  },
  math: {
    name: 'Math Equations',
    content: `# Math Equations with KaTeX

## Inline Math

The famous equation $E = mc^2$ shows the relationship between energy and mass.

The quadratic formula is $x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}$.

## Display Math

The Pythagorean theorem:

$$
a^2 + b^2 = c^2
$$

Euler's identity:

$$
e^{i\\pi} + 1 = 0
$$

The Gaussian integral:

$$
\\int_{-\\infty}^{\\infty} e^{-x^2} dx = \\sqrt{\\pi}
$$

Maxwell's equations:

$$
\\begin{aligned}
\\nabla \\cdot \\mathbf{E} &= \\frac{\\rho}{\\epsilon_0} \\\\
\\nabla \\cdot \\mathbf{B} &= 0 \\\\
\\nabla \\times \\mathbf{E} &= -\\frac{\\partial \\mathbf{B}}{\\partial t} \\\\
\\nabla \\times \\mathbf{B} &= \\mu_0\\mathbf{J} + \\mu_0\\epsilon_0\\frac{\\partial \\mathbf{E}}{\\partial t}
\\end{aligned}
$$

## Matrices

$$
\\begin{bmatrix}
a & b \\\\
c & d
\\end{bmatrix}
\\begin{bmatrix}
x \\\\
y
\\end{bmatrix}
=
\\begin{bmatrix}
ax + by \\\\
cx + dy
\\end{bmatrix}
$$
`,
  },
  comprehensive: {
    name: 'Comprehensive Example',
    content: `# Comprehensive Markdown Demo

## Table of Contents
1. [Text Formatting](#text-formatting)
2. [Lists](#lists)
3. [Code](#code)
4. [Tables](#tables)
5. [Diagrams](#diagrams)

---

## Text Formatting

**Bold**, *italic*, ~~strikethrough~~, \`inline code\`, and [links](https://github.com).

### Nested Formatting

***Bold and italic*** and **bold with *italic* inside**.

## Lists

### Unordered List
- First item
- Second item
  - Nested item
  - Another nested item
- Third item

### Ordered List
1. First step
2. Second step
3. Third step

### Task List
- [x] Completed task
- [ ] Pending task
- [ ] Another task

## Code

### JavaScript
\`\`\`javascript
const greeting = (name) => {
  return \`Hello, \${name}!\`;
};

console.log(greeting('World'));
\`\`\`

### Python
\`\`\`python
def fibonacci(n):
    if n <= 1:
        return n
    return fibonacci(n-1) + fibonacci(n-2)

print([fibonacci(i) for i in range(10)])
\`\`\`

## Tables

| Language | Type | Popular Use |
|----------|------|-------------|
| JavaScript | Dynamic | Web Development |
| Python | Dynamic | Data Science |
| Rust | Static | Systems Programming |
| Go | Static | Backend Services |

## Diagrams

\`\`\`mermaid
graph LR
    A[Input] --> B[Processing]
    B --> C{Decision}
    C -->|Yes| D[Output A]
    C -->|No| E[Output B]
\`\`\`

## Math

The quadratic formula: $x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}$

Display equation:

$$
\\sum_{i=1}^{n} i = \\frac{n(n+1)}{2}
$$

## Blockquotes

> This is a blockquote.
> It can span multiple lines.
>
> > And can be nested too!

## Images

![Markdown Logo](https://markdown-here.com/img/icon256.png)

---

**Made with ❤️ using Markdawn**
`,
  },
};
